// MapComponent.jsx
import React, { useEffect, useState, useCallback, useRef, forwardRef } from "react";
import axios from "axios";

import { MapContainer, TileLayer, GeoJSON, Marker, useMap, Popup } from "react-leaflet";
import L from "leaflet";
import GoogleMutantLayer from "./GoogleMutantLayer";
import useGoogleMaps from "../hooks/useGoogleMaps";
import "../components/styles/MapComponent.css";
import Legend from './Legend';



const territoryColors = {
  'CARNAUBAIS': '#E41A1C', // Vermelho
  'CHAPADA DAS MANGABEIRAS': '#377EB8', // Azul
  'CHAPADA VALE DO ITAIM': '#4DAF4A', // Verde
  'COCAIS': '#984EA3', // Roxo
  'ENTRE RIOS': '#FF7F00', // Laranja
  'PLANICIE LITORANEA': '#FFFF33', // Amarelo 
  'SERRA DA CAPIVARA': '#A65628', // Marrom
  'TABULEIROS DO ALTO PARNAÍBA': '#F781BF', // Rosa
  'VALE DO CANINDE': '#999999', // Cinza
  'VALE DO RIO GUARIBAS': '#66C2A5', // Verde-água
  'VALE DO SAMBITO': '#FC8D62', // Pêssego
  'VALE DOS RIOS PIAUI E ITAUEIRAS': '#8da0cb', // Azul claro

};

const statusColors = {
  'Coleta Recusada': '#faad83',
  'Concluído': '#00fff7',
  'Correção de coleta': '#9d00ffcc',
  'Apto para Migração': '#4eb2ec',
  'Iniciado': '#949895',
  'Em análise': '#000000',
  'Regularizado Externamente': '#79e781',
  'Casa Fechada': '#ff8000',
  'Cancelado': '#f68f8fe0',
  'Coleta Segunda Etapa': '#d4d4d4',
  'Processo Administrativo': '#643a3a',
  'Análise Jurídica': '#f6ff00',
  'Validação Jurídica': '#cada14',
  'Enviado ao CerurbJus': '#1638b4',
  'Regularizado': '#279916ba',
  'Pendente': '#ff0000',
  'Memorial Para Correção': '#ff4af6c5',
  'Inconsistência no sistema da ADH': '#8a6565',
  'Bloqueado': '#9f0000',
  'Correção de Mapa': '#a98cffbf',
  'DEFAULT': '#f4f7fc'
};

const NUCLEO_MARKER_ICON = L.icon({
  iconUrl: "/alfinet_prourb.png",
  iconSize: [20, 25],
  iconAnchor: [10, 25],
});

const DETAIL_MARKER_ICON = L.icon({
  iconUrl: "/alfinet_prourb.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const formatPropertyName = (name) => {
  return name
    .replace(/_/g, ' ')
    .replace(/(?:^|\s)\S/g, a => a.toUpperCase());
};



// Ajustar bounds
const FitBoundsHandler = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { animate: false });
    }
  }, [bounds, map]);

  return null;
};

const MapInstanceHandler = ({ onReady }) => {
  const map = useMap();

  useEffect(() => {
    onReady(map);
    return () => onReady(null);
  }, [map, onReady]);

  return null;
};

// Capturar zoom e redimensionar mapa
const MapEventHandler = ({ setZoomLevel }) => {
  const map = useMap();

  useEffect(() => {
    const handleZoom = () => setZoomLevel(map.getZoom());
    map.on('zoomend', handleZoom);
    setZoomLevel(map.getZoom());
    return () => map.off('zoomend', handleZoom);
  }, [setZoomLevel, map]);

  useEffect(() => {
    const container = map.getContainer();
    let timer;
    let lastW = container.offsetWidth;
    let lastH = container.offsetHeight;
    const observer = new ResizeObserver(() => {
      const w = container.offsetWidth;
      const h = container.offsetHeight;
      if (w === lastW && h === lastH) return;
      lastW = w; lastH = h;
      clearTimeout(timer);
      timer = setTimeout(() => { map.invalidateSize(); }, 150);
    });
    observer.observe(container);
    return () => { observer.disconnect(); clearTimeout(timer); };
  }, [map]);

  return null;
};

// Controla visibilidade dos labels de território via CSS (sem openTooltip/closeTooltip)
const TerritorioLabelVisibilityHandler = ({ threshold = 6 }) => {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const update = () => {
      if (map.getZoom() > threshold) {
        container.classList.add('show-territory-labels');
      } else {
        container.classList.remove('show-territory-labels');
      }
    };
    map.on('zoomend', update);
    update();
    return () => map.off('zoomend', update);
  }, [map, threshold]);
  return null;
};



// Componente para exibir informações do núcleo externo
const MapComponent = forwardRef(({ lotes = [], quadras = [], nucleos = [], municipios = [], territorios = [],
  selectedTerritorio, selectedMunicipio, selectedNucleo = "Todos", bounds, layers, boundsKey, onFeatureSelect, onNucleoInfoClick, loadingDetails = false, activeView = 'painel1' }, ref) => {

  const mapRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const lotesLayerRef = useRef(null);
  const loteLabelLayersRef = useRef(new Set());
  const quadrasLayerRef = useRef(null);
  const nucleosLayerRef = useRef(null);
  
  const territoriosLayerRef = useRef(null);

  const [zoomLevel, setZoomLevel] = useState(7);
  const isZoomedIn = zoomLevel >= 13;

  const [legendsVisible, setLegendsVisible] = useState(false);
  const [popupContent, setPopupContent] = useState(null);
  const [baseMap, setBaseMap] = useState("osm");

  
  const apiLoaded = useGoogleMaps(
    ["google", "google-hybrid"].includes(baseMap)
      ? process.env.REACT_APP_GOOGLE_MAPS_API_KEY
      : null
  );


  const handleMapReady = useCallback((map) => {
    mapRef.current = map;
    setMapInstance(map);
  }, []);
  

  const normalizeString = (s) =>
  s ? s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().trim().replace(/\s+/g, " ") : "";

  


      // Estilo dinâmico para GeoJSON
      const getStyle = useCallback((feature) => {
        if (!feature?.properties) return {};

        const tipo = feature.properties.TIPO;
        
        const isTerritorioFiltrado = selectedTerritorio && selectedTerritorio !== "Todos";
        const isMunicipioFiltrado = selectedMunicipio && selectedMunicipio !== "Todos";

        const nenhumFiltroAtivo = !isTerritorioFiltrado && !isMunicipioFiltrado;

        let isOutOfFocus = false;

        if (!nenhumFiltroAtivo) {
          const nomeMunFeature = normalizeString(feature.properties.MUNICIPIO);
          const nomeTerrFeature = normalizeString(feature.properties.TERRITORIO);
          
          const filtroMun = normalizeString(selectedMunicipio);
          const filtroTerr = normalizeString(selectedTerritorio);

          // --- LOGICA DE PRIORIDADE ---
          if (isMunicipioFiltrado) {
            // Se um município está selecionado, QUALQUER coisa que não seja 
            // exatamente esse município deve ser esmaecida.
            isOutOfFocus = nomeMunFeature !== filtroMun;
          } 
          else if (isTerritorioFiltrado) {
            // Se não há município selecionado, mas há território, 
            // esmaece o que não pertence ao território.
            isOutOfFocus = nomeTerrFeature !== filtroTerr;
          }
        }

        // Se estiver fora de foco, aplica o estilo cinza transparente

        // --- ESTILO COLORIDO (Destaque ou Sem Filtro) ---
        switch(tipo) {        
        case 'LOTE':
          const status = feature.properties.SITUACAO;
          const StatusfillColor = statusColors[status] || statusColors.DEFAULT;
          return {
            color: '#a83232',
            weight: 1,
            fillColor: StatusfillColor,
            fillOpacity: zoomLevel >= 14 ? 0.2 : 0.4
          };

        case 'QUADRA':
          return {
            color: '#1929d7',
            weight: 1,
            fillColor: 'transparent',
            fillOpacity: zoomLevel >= 13 ? 0.7 : 0.4
          };
        case 'NUCLEO':
          return {
            color: '#00FF00',
            fillColor: 'transparent',           
            weight: 1,
            fillOpacity: zoomLevel >= 12 ? 0.7 : 0.4
          };
          case 'MUNICIPIO':
            if (isOutOfFocus) {
              return {
                color: '#d1d1d1',
                weight: 1,
                fillColor: '#e0e0e0',
                fillOpacity: 0.05, 
              };
            }
            // Se estiver em destaque, vamos dar uma borda um pouco mais forte
            return {
              color: isMunicipioFiltrado ? '#333' : '#b1b4b1ff', 
              weight: isMunicipioFiltrado ? 1.8 : 1, 

              fillColor: '#cccff8', 
              fillOpacity: isMunicipioFiltrado ? 0.4 : 0.15 
            };

          case 'TERRITORIO':
            if (isOutOfFocus) {
              return {
                color: '#d1d1d1',
                weight: 1,
                fillColor: '#e0e0e0',
                fillOpacity: 0.05, 
              };
            }
            const territoryName = feature.properties.TERRITORIO;
            const tColor = territoryColors[territoryName] || territoryColors.DEFAULT;
            return {
              color: tColor,
              weight: 1,
              fillColor: tColor,
              fillOpacity: zoomLevel >= 13 ? 0.1 : 0.3  
            };

          default:
            return { color: '#607D8B', weight: 1, fillOpacity: 0.5 };
        }
      }, [zoomLevel, selectedTerritorio, selectedMunicipio]);

  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current.invalidateSize();
      }, 400); // ligeiramente após o ajuste de layout
    }
  }, [boundsKey]);

  

  useEffect(() => {
    const map = mapInstance;
    const layerGroup = lotesLayerRef.current;
    const activeLabels = loteLabelLayersRef.current;
    const MAX_VISIBLE_LOTE_LABELS = 250;

    const clearLabels = () => {
      activeLabels.forEach(layer => layer.unbindTooltip());
      activeLabels.clear();
    };

    const updateVisibleLabels = () => {
      if (!map || !layerGroup?.eachLayer || !layers?.lotes || map.getZoom() < 19) {
        clearLabels();
        return;
      }

      // Rótulos são criados apenas para os lotes no enquadramento atual. O limite
      // impede travamentos caso uma vista excepcionalmente densa seja exibida.
      const visibleBounds = map.getBounds();
      const nextLabels = new Set();

      layerGroup.eachLayer(layer => {
        if (nextLabels.size >= MAX_VISIBLE_LOTE_LABELS || !layer.feature?.properties) return;

        const layerBounds = layer.getBounds?.();
        if (!layerBounds || !visibleBounds.intersects(layerBounds)) return;

        const lote = layer.feature.properties.LOTE;
        nextLabels.add(layer);
        if (!activeLabels.has(layer)) {
          layer.bindTooltip(`L: ${lote || '-'}`, {
            permanent: true,
            direction: 'center',
            className: 'lote-label'
          }).openTooltip();
        }
      });

      activeLabels.forEach(layer => {
        if (!nextLabels.has(layer)) layer.unbindTooltip();
      });

      activeLabels.clear();
      nextLabels.forEach(layer => activeLabels.add(layer));
    };

    let animationFrame;
    const scheduleUpdate = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(updateVisibleLabels);
    };

    scheduleUpdate();
    map?.on('moveend zoomend', scheduleUpdate);

    return () => {
      cancelAnimationFrame(animationFrame);
      map?.off('moveend zoomend', scheduleUpdate);
      clearLabels();
    };
  }, [mapInstance, zoomLevel, layers?.lotes, lotes]);


  useEffect(() => {
    const layerGroup = quadrasLayerRef.current;

    if (layers?.quadras && layerGroup && layerGroup.getLayers) {
      layerGroup.getLayers().forEach(layer => {
        const props = layer.feature?.properties;
        if (props?.TIPO === 'QUADRA') {
          const label = `Q: ${props.QUADRA}`;

          if (zoomLevel >= 16) {
            layer.bindTooltip(label, {
              permanent: true,
              direction: 'center',
              className: 'quadra-label'
            }).openTooltip();
          } else {
            layer.unbindTooltip();
          }
        }
      });
    }
  }, [zoomLevel, layers?.quadras]);


  useEffect(() => {
    const layerGroup = nucleosLayerRef.current;

    if (layers?.nucleos && layerGroup && layerGroup.getLayers) {
      layerGroup.getLayers().forEach(layer => {
        const props = layer.feature?.properties;
        if (props?.TIPO === 'NUCLEO') {
          const label = props.NUCLEO;

          if (zoomLevel >= 15) {
            layer.bindTooltip(label, {
              permanent: true,
              direction: 'center',
              className: 'nucleo-label'
            }).openTooltip();
          } else {
            layer.unbindTooltip();
          }
        }
      });
    }
  }, [zoomLevel, layers?.nucleos]);






  // Manipulador de clique para GeoJSON
  const onEachFeature = useCallback((feature, layer) => {
    // Versão simplificada do getFieldsToShow específica para o popup
    const getPopupFields = (feature) => {
      if (!feature?.properties) return {};
            
      const tipo = feature.properties?.TIPO;
      const allProperties = feature.properties || {};
      const baseFields = {
        'Tipo': tipo?.charAt(0).toUpperCase() + tipo?.slice(1),
      };
      const specificFields = {};
      
      switch(tipo) {
        case 'LOTE':
          Object.assign(specificFields, {
            'Município': allProperties.MUNICIPIO,
            'Núcleo': allProperties.NUCLEO,
            'Área (m²)': allProperties.AREA != null ? parseFloat(allProperties.AREA).toFixed(2) : null,
            'Perímetro (m)': allProperties.PERIMETRO != null ? parseFloat(allProperties.PERIMETRO).toFixed(2) : null,
            'Lote': allProperties.LOTE,
            'Quadra': allProperties.QUADRA,
            'Status': allProperties.SITUACAO,
          });
          break;
        case 'QUADRA':
          Object.assign(specificFields, {
            'Município': allProperties.MUNICIPIO,
            'Núcleo': allProperties.NUCLEO,
            'Área (m²)': allProperties.AREA != null ? parseFloat(allProperties.AREA).toFixed(2) : null,
            'Perímetro (m)': allProperties.PERIMETREO != null ? parseFloat(allProperties.PERIMETREO).toFixed(2) : null,
            'Lote': allProperties.LOTE,
            'Quadra': allProperties.QUADRA,
          });
          break;
          
        case 'NUCLEO':
          Object.assign(specificFields, {
            'Núcleo': allProperties.NUCLEO,
            'Município': allProperties.MUNICIPIO,
          });
          break;
          
        case 'MUNICIPIO':
          Object.assign(specificFields, {
            'Nome': allProperties.MUNICIPIO,
            'população (IBGE 2022) ': allProperties.POPULACAO,
            'Domicílios (IBGE 2022)': allProperties.DOMICILIOS,
            'Território de desenvolvimento': allProperties.TERRITORIO,
          });
          break;
        
          case 'TERRITORIO':
          Object.assign(specificFields, {
            'Território de desenvolvimento': allProperties.TERRITORIO,
          });
          break;
          
        default:
          Object.keys(allProperties).forEach(key => {
            if (key !== 'TIPO' && key !== 'id' && key !== 'gid') {
              specificFields[formatPropertyName(key)] = allProperties[key];
            }
          });
      }
      
      return { ...baseFields, ...specificFields };
    };

    const fieldsToShow = getPopupFields(feature);

    // Cria o conteúdo do popup usando os campos filtrados
    const popupContent = `
      <div style="max-width: 250px; font-family: Arial, sans-serif; font-size: 12px;">
        <h3 style="margin-top: 0; margin-bottom: 10px; border-bottom: 1px solid #eee; padding-bottom: 5px;">
          ${feature.properties.TIPO?.toUpperCase() || 'FEIÇÃO'}
        </h3>
        <table style="width: 100%; border-collapse: collapse;">
          ${Object.entries(fieldsToShow).map(([key, value]) => `
            <tr style="border-bottom: 1px solid #eee;">
              <td style="padding: 3px 0; vertical-align: top;"><strong>${key}:</strong></td>
              <td style="padding: 3px 0; vertical-align: top;">${value?.toString() || 'N/A'}</td>
            </tr>
          `).join('')}
        </table>
      </div>
    `;
    if (feature.properties?.TIPO === 'TERRITORIO') {
      const label = feature.properties.TERRITORIO;
      if (label) {
        const color = territoryColors[label] || '#ffffff';
        layer.bindTooltip(`<span style="color:${color}">${label}</span>`, {
          permanent: true, direction: 'top', className: 'territorio-label',
        });
      }
    }

    // Vincula o popup à camada
    layer.bindPopup(popupContent, {
      closeButton: true,
      autoClose: false,
      closeOnClick: false,
      className: 'custom-popup'
    });

    layer.on({
      click: (e) => {
        L.DomEvent.stopPropagation(e);

        // Timer para distinguir clique simples de clique duplo
        if (layer._clickTimer) {
          clearTimeout(layer._clickTimer);
          layer._clickTimer = null;
          return; // segundo clique — o dblclick vai tratar
        }

        layer._clickTimer = setTimeout(() => {
          layer._clickTimer = null;
          // Clique simples: abre popup e feedback visual
          layer.openPopup(e.latlng);
          layer.setStyle({ weight: 3, color: '#ff0303', fillOpacity: 0.2 });
          setTimeout(() => { layer.setStyle(getStyle(feature)); }, 1000);
          // Para núcleos: abre painel de informações/edição
          if (feature.properties?.TIPO === 'NUCLEO' && onNucleoInfoClick) {
            onNucleoInfoClick(feature.properties);
          }
        }, 250);
      },
      dblclick: (e) => {
        L.DomEvent.stopPropagation(e);
        L.DomEvent.preventDefault(e);

        // Clique duplo: filtra a feição
        if (onFeatureSelect && feature.properties) {
          const props = feature.properties;
          onFeatureSelect({
            tipo: props.TIPO,
            territorio: props.TERRITORIO,
            municipio: props.MUNICIPIO,
            nucleo: props.NUCLEO
          });
        }
      },
      mouseover: () => layer.setStyle({ weight: 2, color: '#ff0303' }),
      mouseout: () => layer.setStyle(getStyle(feature))
    });

    if (feature.properties?.TIPO === 'LOTE') {
      layer.options.loteId = feature.properties.lote;
    }
    if (feature.properties?.TIPO === 'QUADRA') {
      layer.options.quadraLabel = feature.properties.quadra;
    }

  },[onFeatureSelect, onNucleoInfoClick, getStyle]);
  
  // Manipulador de clique para Markers
  const handleMarkerClick = useCallback((feature) => {      
    // setSelectedFeature(feature);
    setPopupContent(
      <Popup>
        <div>
          <h4>{feature.properties?.TIPO || 'Feição'}</h4>
          {Object.entries(feature.properties || {}).map(([key, value]) => (
            <p key={key}><strong>{key}:</strong> {value?.toString() || 'N/A'}</p>
          ))}
        </div>
      </Popup>
    );
  }, []);

  const handleDownloadShapefile = async () => {
    const API_URL = process.env.REACT_APP_API_URL;
    const API_KEY = process.env.REACT_APP_API_KEY;
    const campo = activeView === 'painel1' ? 'NUCLEO_CASALEGAL_PAINEIS' : activeView === 'painel2' ? 'NM_CERURB' : 'NUCLEO';

    const params = selectedNucleo !== "Todos"
      ? { nucleo: selectedNucleo, campo }
      : { municipio: selectedMunicipio, campo };

    setDownloading(true);
    try {
      const response = await axios.get(`${API_URL}/dados/nucleos/download`, {
        headers: { Authorization: `Bearer ${API_KEY}` },
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
        const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const name = match ? match[1] : (selectedNucleo !== "Todos" ? selectedNucleo : selectedMunicipio)
        .replace(/\s+/g, '_').substring(0, 50) + '.zip';
      link.setAttribute('download', name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data ? await err.response.data.text?.() : null;
      alert(msg ? JSON.parse(msg)?.message : 'Erro ao gerar shapefile.');
    } finally {
      setDownloading(false);
    }
  };

  const showDownloadBtn = selectedMunicipio !== "Todos" || selectedNucleo !== "Todos";

  return (
    <>
      <div className="base-map-selector">
        <label>
          <strong>Mapa base:</strong>{" "}
          <select
            value={baseMap}
            onChange={(e) => setBaseMap(e.target.value)}
            style={{ marginLeft: "10px",width: "80px", fontSize: "12px", padding: "2px" }}
          >
            <option value="carto">CartoDB Light</option>
            <option value="osm">OpenStreetMap</option>
            <option value="google">Google Maps</option>
            <option value="google-hybrid">Google Satelite</option>
          </select>
        </label>
      </div>

      <MapContainer
        className="map-container"
        // style={{ height: "100%", width: "100%" }}
        center={[-7.5, -42.5]}
        zoom={7}
        minZoom={5}
        maxZoom={22}
        scrollWheelZoom={true}
        zoomControl={false}
        doubleClickZoom={false}
        whenCreated={(mapInstance) => (mapRef.current = mapInstance)}

      >

        <MapInstanceHandler onReady={handleMapReady} />

      {baseMap === "carto" && (
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url={`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_API_KEY}`}
                subdomains="abcd"
                maxZoom={20}
              />
            )}
      {baseMap === "osm" && (
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
      )}

      {["google", "google-hybrid"].includes(baseMap) && apiLoaded && (
        <GoogleMutantLayer type={baseMap === "google" ? "roadmap" : "hybrid"} />
      )}

        <MapEventHandler setZoomLevel={setZoomLevel} />
        <TerritorioLabelVisibilityHandler threshold={6.5} />

        {loadingDetails && (
          <div className="map-details-loading" role="status" aria-live="polite">
            <span className="map-details-loading__spinner" aria-hidden="true" />
            Carregando lotes e quadras...
          </div>
        )}

      {/* --- CONTAINER DAS LEGENDAS À ESQUERDA --- */}
      {/* Este 'div' é crucial. Ele usa a classe 'legend-column-left' 
        do seu CSS para flutuar sobre o mapa (position: absolute, z-index: 1000)
      */}
      <div className="legend-column-left">
        <button 
            className="legend-toggle-btn" 
            onClick={() => setLegendsVisible(!legendsVisible)}
          >
            {legendsVisible ? 'Esconder Legendas' : 'Mostrar Legendas'}
          </button>
        
          {legendsVisible && (
            <>
              {/* TERRITORIOS LEGENDA */}
              {layers?.territorios && (
                <Legend
                  colors={territoryColors}
                  title="Territórios"
                />
              )}

              {/* LOTES LEGENDA */}
              {layers?.lotes && (
                <Legend
                  colors={statusColors}
                  title="Status dos Lotes"
                />
              )}
            </>
          )}

          {showDownloadBtn && (
            <button
              onClick={handleDownloadShapefile}
              disabled={downloading}
              title="Baixar shapefile do núcleo/município selecionado"
              style={{
                marginTop: 8,
                width: '100%',
                padding: '5px 0',
                background: downloading ? '#78909c' : '#1565c0',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: downloading ? 'default' : 'pointer',
                fontSize: 11,
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
              }}
            >
              {downloading ? '⏳ Gerando...' : '⬇ Baixar Shape'}
            </button>
          )}

        </div>
        
        {layers?.municipios && municipios.length > 0 && (
          <GeoJSON
            key={`municipios-layer-${municipios.length}-${selectedTerritorio}-${selectedMunicipio}`}
            data={{ type: "FeatureCollection", features: municipios }}
            style={getStyle}
            onEachFeature={onEachFeature}
          />
        )}

        {/* TERRITÓRIOS */}
        {layers?.territorios && territorios.length > 0 && (
          <GeoJSON
            key={`territorios-layer-${territorios.length}-${selectedTerritorio}-${isZoomedIn}`}
            data={{ type: "FeatureCollection", features: territorios }}
            style={getStyle}
            onEachFeature={onEachFeature}
            ref={territoriosLayerRef}
          />
        )}

        {/* NÚCLEOS */}
        {layers?.nucleos && nucleos.length > 0 && (
          zoomLevel < 12 ? (
            nucleos.map((feature, index) => {
              if (!feature || !feature.geometry) return null;
            
              let coords = null;
            
              if (feature.geometry.type === "Point") {
                coords = feature.geometry.coordinates;
              } else if (feature.geometry.type === "Polygon") {
                coords = feature.geometry.coordinates[0][0];
              } else if (feature.geometry.type === "MultiPolygon") {
                coords = feature.geometry.coordinates[0][0][0];
              }
            
              if (!coords) return null;
            
              const [lng, lat] = coords;
            
              return (
                <Marker
                  key={`nucleo-marker-${index}`}
                  position={[lat, lng]}
                  icon={NUCLEO_MARKER_ICON}
                  eventHandlers={{
                    click: () => handleMarkerClick(feature)
                  }}
                >
                  {popupContent}
                </Marker>
              );
            })          
          ) : (
            <GeoJSON
              key={`nucleos-layer-${nucleos.length}-${selectedTerritorio}-${selectedMunicipio}`}
              data={{ type: "FeatureCollection", features: nucleos }}
              style={getStyle}
              onEachFeature={onEachFeature}
              ref={nucleosLayerRef}
            />
          )
        )}



        {/* QUADRAS */}
        {layers?.quadras && quadras.length > 0 && (
          zoomLevel < 13 ? (
            quadras.map((feature, index) => {
              if (!feature || !feature.geometry) return null; 
              
              const coords = feature.geometry.type === "Point"
                ? feature.geometry.coordinates
                : feature.geometry.type === "Polygon"
                  ? feature.geometry.coordinates[0][0]
                  : null;
              
              if (!coords) return null;
                const [lng, lat] = coords;
              
              return (
                <Marker
                  key={`lote-marker-${index}`}
                  position={[lat, lng]}
                  icon={DETAIL_MARKER_ICON}
                  eventHandlers={{
                    click: () => handleMarkerClick(feature)
                  }}
                >
                  {popupContent}
                </Marker>
              );
            })
          ) : (
          <GeoJSON
            key={`quadras-layer-${quadras.length}-${quadras[0]?.properties?.id ?? ''}`}
            data={{ type: "FeatureCollection", features: quadras }}
            style={getStyle}
            onEachFeature={onEachFeature}
            ref={quadrasLayerRef}
          />

          )
        )}

        {/* LOTES */}
        {layers?.lotes && lotes.length > 0 && (
          zoomLevel < 16? (
            lotes.map((feature, index) => {
              if (!feature || !feature.geometry) return null; 
              
              const coords = feature.geometry.type === "Point"
                ? feature.geometry.coordinates
                : feature.geometry.type === "Polygon"
                  ? feature.geometry.coordinates[0][0]
                  : null;
              
              if (!coords) return null;
                const [lng, lat] = coords;
              
              return (
                <Marker
                  key={`lote-marker-${index}`}
                  position={[lat, lng]}
                  icon={DETAIL_MARKER_ICON}
                  eventHandlers={{
                    click: () => handleMarkerClick(feature)
                  }}
                >
                  {popupContent}
                </Marker>
              );
            })
          ) : (
            <GeoJSON
              key={`lotes-layer-${lotes.length}-${lotes[0]?.properties?.id ?? ''}`}
              data={{ type: "FeatureCollection", features: lotes }}
              style={getStyle}
              onEachFeature={onEachFeature}
              ref={lotesLayerRef}
            />
          )
        )}





        <FitBoundsHandler key={`bounds-${boundsKey}`} bounds={bounds} />


      </MapContainer>


    </>
  );
});

export default MapComponent;
